import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import { num } from "@/lib/format";
import { toast } from "sonner";
import { Play, ArrowRightCircle } from "lucide-react";

export function MrpPanel() {
  const { activeSite } = useSite();
  const navigate = useNavigate();
  const [run, setRun] = useState(null);
  const [planned, setPlanned] = useState([]);
  const [busy, setBusy] = useState(false);

  const loadPlanned = () => api.get(`/planned-orders?site=${activeSite}`).then((r) => setPlanned(r.data)).catch(() => setPlanned([]));
  useEffect(() => { loadPlanned(); }, [activeSite]);

  const runMrp = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/mrp/run?site=${activeSite}`);
      setRun(data);
      toast.success(`MRP run complete · ${data.shortages} shortages`);
      loadPlanned();
    } catch (e) { toast.error(e?.response?.data?.detail || "MRP run failed"); }
    finally { setBusy(false); }
  };

  const convert = async (p) => {
    try {
      const { data } = await api.post(`/planned-orders/${p.id}/convert`);
      toast.success(`${data.kind === "production_order" ? "Production order" : "Requisition"} ${data.ref} created`);
      loadPlanned();
    } catch (e) { toast.error(e?.response?.data?.detail || "Convert failed"); }
  };

  const rows = run?.rows || [];
  return (
    <div className="space-y-5" data-testid="mrp-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-[#e5e9f0] bg-white p-4 shadow-sm">
        <div>
          <div className="text-sm font-semibold text-[#32363a]">Material Requirements Planning · EP-PP04 · <span className="font-normal text-[#9aa0a6]">S/4 MD01</span></div>
          <div className="text-xs text-[#6a6d70]">Nets demand (open sales orders) against stock and open receipts, then proposes planned orders for shortages.</div>
        </div>
        <button data-testid="mrp-run-btn" onClick={runMrp} disabled={busy} className="inline-flex items-center gap-1.5 rounded-md bg-[#3B82F6] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2563eb] disabled:opacity-60">
          <Play size={16} /> {busy ? "Running…" : "Run MRP"}</button>
      </div>

      {run && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <div className="rounded-lg border border-[#e5e9f0] bg-white p-3"><div className="text-[11px] uppercase text-[#6a6d70]">Materials Planned</div><div className="text-2xl font-semibold text-[#3B82F6]">{rows.length}</div></div>
          <div className="rounded-lg border border-[#e5e9f0] bg-white p-3"><div className="text-[11px] uppercase text-[#6a6d70]">Shortages</div><div className="text-2xl font-semibold text-[#EF4444]">{run.shortages}</div></div>
          <div className="rounded-lg border border-[#e5e9f0] bg-white p-3"><div className="text-[11px] uppercase text-[#6a6d70]">Scope</div><div className="text-2xl font-semibold text-[#32363a]">{run.site}</div></div>
          <div className="rounded-lg border border-[#e5e9f0] bg-white p-3"><div className="text-[11px] uppercase text-[#6a6d70]">Planned Orders</div><div className="text-2xl font-semibold text-[#10B981]">{planned.filter((p) => p.status === "PLANNED").length}</div></div>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-[#e5e9f0] bg-white shadow-sm" data-testid="mrp-table">
        <div className="border-b border-[#eff1f4] px-4 py-3 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]">MRP List</div>
        <table className="w-full text-left text-sm">
          <thead><tr className="border-b border-[#e5e9f0] bg-[#f7f8fa] text-[11px] uppercase tracking-wide text-[#6a6d70]"><th className="px-3 py-2.5">Material</th><th className="px-3 py-2.5">Type</th><th className="px-3 py-2.5">Demand</th><th className="px-3 py-2.5">On Hand</th><th className="px-3 py-2.5">Receipts</th><th className="px-3 py-2.5">Net Req.</th></tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={`${r.material_code}-${r.site_id}`} className={`border-b border-[#eff1f4] last:border-0 ${r.shortage ? "bg-red-50/40" : ""}`}>
                <td className="px-3 py-2"><div>{r.material_name}</div><div className="font-mono text-[10px] text-[#6a6d70]">{r.material_code} · {r.site_id}</div></td>
                <td className="px-3 py-2 font-mono text-xs">{r.type}</td>
                <td className="px-3 py-2 font-mono">{num(r.demand)}</td>
                <td className="px-3 py-2 font-mono">{num(r.on_hand)}</td>
                <td className="px-3 py-2 font-mono">{num(r.receipts)}</td>
                <td className={`px-3 py-2 font-mono font-semibold ${r.shortage ? "text-red-600" : "text-emerald-600"}`}>{r.net_requirement > 0 ? `-${num(r.net_requirement)}` : num(-r.net_requirement)}</td>
              </tr>))}
            {rows.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-[#6a6d70]">Run MRP to generate the requirements list.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="overflow-x-auto rounded-lg border border-[#e5e9f0] bg-white shadow-sm" data-testid="planned-orders-table">
        <div className="border-b border-[#eff1f4] px-4 py-3 text-sm font-semibold uppercase tracking-wide text-[#6a6d70]">Planned Orders</div>
        <table className="w-full text-left text-sm">
          <thead><tr className="border-b border-[#e5e9f0] bg-[#f7f8fa] text-[11px] uppercase tracking-wide text-[#6a6d70]"><th className="px-3 py-2.5">Material</th><th className="px-3 py-2.5">Qty</th><th className="px-3 py-2.5">Procurement</th><th className="px-3 py-2.5">Status</th><th className="px-3 py-2.5">Ref</th><th /></tr></thead>
          <tbody>
            {planned.map((p) => (
              <tr key={p.id} className="border-b border-[#eff1f4] last:border-0">
                <td className="px-3 py-2"><div>{p.material_name}</div><div className="font-mono text-[10px] text-[#6a6d70]">{p.material_code} · {p.site_id}</div></td>
                <td className="px-3 py-2 font-mono">{num(p.qty)} {p.uom}</td>
                <td className="px-3 py-2 text-xs">{p.proc_type === "in-house" ? "In-house (PP)" : "External (MM)"}</td>
                <td className="px-3 py-2"><span className={`rounded px-2 py-0.5 text-[11px] font-medium ${p.status === "CONVERTED" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{p.status}</span></td>
                <td className="px-3 py-2 font-mono text-[10px] text-[#3B82F6]">{p.ref || "—"}</td>
                <td className="px-3 py-2">{p.status === "PLANNED" && <button data-testid={`convert-planned-${p.id}`} onClick={() => convert(p)} className="inline-flex items-center gap-1 rounded-md border border-[#c8cfda] bg-white px-2 py-1 text-[11px] hover:border-[#3B82F6]"><ArrowRightCircle size={13} /> {p.proc_type === "in-house" ? "Create Prod Order" : "Create Requisition"}</button>}</td>
              </tr>))}
            {planned.length === 0 && <tr><td colSpan={6} className="px-3 py-8 text-center text-[#6a6d70]">No planned orders yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
